import React, { createContext, useContext } from "react"
import { useActor } from "@xstate/react"
import {
    ECommerceSend,
    ECommerceService,
    ECommerceState,
} from "../../features/_common/machines/ecommerce-machine/e-commerce-machine"
import { IsomorphicRequestResponseChannel } from "../api/isomorphic-request-response-channel"
import { SwitchFlowSend, SwitchFlowService, SwitchFlowState } from "../../features/_common/machines/switch-flow-machine"
import {
    SubmitStatusSend,
    SubmitStatusService,
    SubmitStatusState,
} from "../../features/_common/machines/submit-status-machine"
import { IsomorphicServerNotificationChannel } from "../api/isomorphic-server-notification-channel"
import { WorkQueueSend, WorkQueueService, WorkQueueState } from "./work-queue/work-queue-machine"
import { NotificationSend, NotificationService, NotificationState } from "./notification-machine"

export type MachinesContextType = {
    ecommerce: ECommerceService
    notification: NotificationService
    channel: IsomorphicRequestResponseChannel
    serverNotificationChannel: IsomorphicServerNotificationChannel
    switchFlow: SwitchFlowService
    submitStatus: SubmitStatusService
    workQueue: WorkQueueService
}

const MachinesContext = createContext(
    // Typed this way to avoid TS errors,
    // looks odd I know
    {} as MachinesContextType,
)

type Props = { services: MachinesContextType }
export const MachinesContextProvider: React.FC<React.PropsWithChildren<Props>> = ({ services, children }) => {
    const stableServices = React.useMemo(() => services, Object.values(services))

    return <MachinesContext.Provider value={stableServices}>{children}</MachinesContext.Provider>
}

export const useServices = (): MachinesContextType => useContext(MachinesContext)

export const useECommerceMachine = (): [ECommerceState, ECommerceSend] => {
    const context = useContext(MachinesContext)
    return useActor(context.ecommerce)
}

export const useChannel = (): IsomorphicRequestResponseChannel => {
    const context = useContext(MachinesContext)
    return context.channel
}

export const useServerNotificationChannel = (): IsomorphicServerNotificationChannel => {
    const context = useContext(MachinesContext)
    return context.serverNotificationChannel
}

export const useNotificationMachine = (): [NotificationState, NotificationSend] => {
    const context = useContext(MachinesContext)
    return useActor(context.notification)
}

export const useSwitchFlowMachine = (): [SwitchFlowState, SwitchFlowSend] => {
    const context = useContext(MachinesContext)
    return useActor(context.switchFlow)
}

export const useWorkQueueMachine = (): [WorkQueueState, WorkQueueSend] => {
    const context = useContext(MachinesContext)
    return useActor(context.workQueue)
}

export const useSubmitStatusMachine = (): [SubmitStatusState, SubmitStatusSend] => {
    const context = useContext(MachinesContext)
    return useActor(context.submitStatus)
}
